<script lang="ts">
	import { Container, SelectV2, Text } from '@Shelly/shelly-design-system';
	import { devices_config } from '@devices/index';
	import { format } from 'svelte-i18n';
	import SelectList from 'src/components/SelectList/SelectList.svelte';
	import { deriveRoomName } from 'src/helpers/service_rooms_helpers';
	import { devices } from 'src/stores/lists/devices';
	import { rooms } from 'src/stores/lists/rooms';
	import { sortListByPossition } from 'src/components/Lists/list-helpers';
	import { getDefaultEnergyConfigForType, getRoomsWithPmDevices } from '../../dash-energy-helpers';
	import type { DashWidgetEditType } from '../../dash-types';
	import type { EnergyHeatmapScope } from 'src/components/EnergyMonitoring/helpers/energy_heatmap_helpers';

	export let editItem: DashWidgetEditType;
	export let compact = false;

	$: if (!editItem.energyConfig) {
		editItem.energyConfig = getDefaultEnergyConfigForType('dashEnergyHeatmap');
	}

	$: scope = (editItem.energyConfig?.heatmapScope ?? 'all') as EnergyHeatmapScope;

	$: scopeOptions = [
		{
			value: 'all',
			title: $format('all_devices', { default: 'All devices' }),
		},
		{
			value: 'room',
			title: $format('wizard_step_room', { default: 'Room' }),
		},
		{
			value: 'device',
			title: $format('label_device', { default: 'Device' }),
		},
	];

	$: selectedScopeOption = scopeOptions.find((option) => option.value === scope) ?? scopeOptions[0];

	const getPmDevicesByRoom = () => {
		const grouped: Record<
			string,
			{
				cards: Array<{ id: string; name: string; icon?: string; image?: string }>;
				roomName: string;
				roomID: string;
				position?: number;
			}
		> = {};

		Object.values($devices).forEach((device) => {
			if (!devices_config[device.type]?.isPM) return;
			const roomID = String(device.room_id);
			const card = {
				id: device.id,
				name: device.name,
				icon: device.icon,
				image: device.image,
			};

			if (grouped[roomID]) {
				grouped[roomID].cards.push(card);
			} else {
				grouped[roomID] = {
					cards: [card],
					roomName: deriveRoomName(device.room_id),
					roomID,
					position: $rooms[device.room_id]?.position,
				};
			}
		});

		return sortListByPossition(Object.values(grouped));
	};

	$: roomList = getRoomsWithPmDevices().map((room) => ({
		...room,
		id: String(room.id),
		type: 'rooms',
		disabled:
			scope === 'room' &&
			editItem.selectItems.length >= 1 &&
			!editItem.selectItems.some((item) => item.id === String(room.id)),
	}));

	$: deviceList = getPmDevicesByRoom().map((room) => ({
		...room,
		cards: room.cards.map((device) => ({
			...device,
			disabled:
				scope === 'device' &&
				editItem.selectItems.length >= 1 &&
				!editItem.selectItems.some((item) => item.id === device.id),
		})),
	}));

	const handleScopeChange = (nextScope: EnergyHeatmapScope) => {
		editItem.energyConfig = {
			...editItem.energyConfig,
			heatmapScope: nextScope,
		};
		editItem.selectItems = [];
	};

	const handleSelectItem = (id: string | number) => {
		const selectedId = String(id);
		const existing = editItem.selectItems.find((entry) => entry.id === selectedId);

		if (existing) {
			editItem.selectItems = editItem.selectItems.filter((entry) => entry.id !== selectedId);
			return;
		}

		editItem.selectItems = [
			{
				type: editItem.widgetType,
				itemType: scope === 'room' ? 'rooms' : 'device',
				id: selectedId,
				position: 0,
			},
		];
	};

	const getOptionValue = (detail: unknown): EnergyHeatmapScope => {
		const value =
			typeof detail === 'string'
				? detail
				: String((detail as { value?: string } | null)?.value ?? 'all');
		return value as EnergyHeatmapScope;
	};
</script>

{#if !compact}
	<Text class="mb-s text-uppercase text-color-muted pl-s-plus" styleType="label-large">
		{$format('dash_energy_heatmap_scope_title', { default: 'Heatmap scope' })}
	</Text>
{/if}

<Container class="d-flex flex-direction-column gap-m">
	<Text styleType="label-large">{$format('label_scope', { default: 'Scope' })}</Text>
	<SelectV2
		id="dash-energy-heatmap-scope"
		icon={null}
		aria-label={$format('dash_energy_heatmap_scope_title', { default: 'Heatmap scope' })}
		size="m"
		width="full"
		label={$format('label_scope', { default: 'Scope' })}
		options={scopeOptions}
		value={selectedScopeOption}
		on:change={(e) => handleScopeChange(getOptionValue(e.detail))}
	/>

	{#if scope === 'all'}
		<Text styleType="body-small" class="text-color-muted">
			{$format('dash_energy_heatmap_scope_all_info', {
				default: 'Shows combined consumption and generation for your entire home.',
			})}
		</Text>
	{:else if scope === 'room'}
		<Text styleType="body-small" class="text-color-muted mb-xs">
			{$format('dash_energy_heatmap_scope_room_info', {
				default: 'Select one room with power-meter devices.',
			})}
		</Text>
		<SelectList
			list={roomList}
			variant="checkbox"
			selectedCardsId={editItem.selectItems}
			on:click={(e) => handleSelectItem(e.detail.id)}
		/>
	{:else}
		<Text styleType="body-small" class="text-color-muted mb-xs">
			{$format('dash_energy_heatmap_scope_device_info', {
				default: 'Select one power-meter device.',
			})}
		</Text>
		<SelectList
			list={deviceList}
			variant="checkbox"
			selectedCardsId={editItem.selectItems}
			on:click={(e) => handleSelectItem(e.detail.id)}
		/>
	{/if}
</Container>
